import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui.js";
import { useAuth, type MembershipState } from "../lib/auth.js";
import { claimFoundingAdmin, needsFoundingAdmin } from "../lib/members.js";

const COPY: Record<string, { title: string; body: string }> = {
  pending: {
    title: "Waiting on an admin",
    body: "Your request is in. You'll see the board as soon as someone approves it — usually the same evening.",
  },
  rejected: {
    title: "This request wasn't approved",
    body: "If you think that's a mistake, talk to whoever runs the group and they can approve you.",
  },
  suspended: {
    title: "Your membership is paused",
    body: "An admin suspended this account. Your matches and rating are untouched.",
  },
};

/**
 * The screen for everyone who is signed in but not playing yet.
 *
 * It also holds the first-run bootstrap: the very first person to sign in has nobody to
 * approve them, so they claim the founding admin role. Once an admin exists the database
 * refuses the claim, and guard_last_admin keeps at least one in existence forever after.
 */
export function Waiting({ state }: { state: MembershipState }) {
  const { signOut, reloadMember, member } = useAuth();
  const copy = COPY[state] ?? COPY["pending"]!;

  const { data: canClaim } = useQuery({
    queryKey: ["founding-admin"],
    queryFn: needsFoundingAdmin,
    enabled: state === "pending",
  });

  const claim = useMutation({
    mutationFn: claimFoundingAdmin,
    onSuccess: reloadMember,
  });

  return (
    <main
      className="mx-auto max-w-md px-4 pb-16"
      style={{ paddingTop: "calc(env(safe-area-inset-top) + 4rem)" }}
    >
      <div className="micro mb-2">{member?.displayName}</div>
      <h1 className="font-display mb-3 text-3xl leading-tight" style={{ fontWeight: 800 }}>
        {copy.title}
      </h1>
      <p className="mb-8 text-ink-muted">{copy.body}</p>

      <div className="tramline mb-8" />

      {canClaim ? (
        <div className="mb-8">
          <p className="mb-4 text-sm" style={{ color: "var(--color-attention)" }}>
            Prestige Hillside Gateway has no admin yet. If you&rsquo;re the one setting it up, take
            the role — you&rsquo;ll approve everyone else.
          </p>
          <Button tone="primary" onClick={() => claim.mutate()} disabled={claim.isPending}>
            {claim.isPending ? "Claiming…" : "I'm setting this up"}
          </Button>
          {claim.error ? (
            <p className="mt-3 text-sm" style={{ color: "var(--color-attention)" }}>
              {claim.error.message}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="flex gap-3">
        <Button onClick={reloadMember}>Check again</Button>
        <Button onClick={() => void signOut()}>Sign out</Button>
      </div>
    </main>
  );
}
